import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bike, Car, Home, Phone, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SeverityPill } from "@/components/app/severity-pill";
import { WeatherIcon } from "@/components/app/WeatherIcon";
import { ProfileCompletionModal, type MissingFields } from "@/components/app/ProfileCompletionModal";
import { liveApi } from "@/lib/api/live";
import { locationApi, weatherApi, authApi } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";
import {
  LIVE_MOVE_METRES,
  LIVE_STALE_MS,
  LIVE_THROTTLE_MS,
  distanceMetres,
  reverseGeocodeMapbox,
} from "@/lib/geo";
import { useOnboardingStore } from "@/stores/onboarding";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/home")({
  head: () => ({
    meta: [
      { title: "Home" },
      { name: "description", content: "Personal flood and weather risk home." },
    ],
  }),
  component: HomeTab,
});

const EMERGENCY_NUMBERS = [
  { label: "Police / Ambulance", number: "999" },
  { label: "Emergency", number: "112" },
  { label: "Kenya Red Cross", number: "1199" },
];

const severityStyle: Record<string, string> = {
  LOW: "border-success/25 bg-success-soft text-success",
  WATCH: "border-watch/25 bg-watch-soft text-watch",
  WARNING: "border-warning/25 bg-warning-soft text-warning",
  SEVERE: "border-severe/25 bg-severe-soft text-severe",
};

function wmoToIcon(code: number): string {
  const h = new Date().getHours();
  const isDay = h >= 6 && h < 18;
  if (code === 0) return isDay ? "clear-day" : "clear-night";
  if (code <= 2) return isDay ? "partly-cloudy-day" : "cloudy";
  if (code === 3) return "cloudy";
  if (code === 45 || code === 48) return "fog";
  if (code >= 51 && code <= 55) return "drizzle";
  if (code >= 61 && code <= 65) return code === 65 ? "heavy-rain" : "rain";
  if (code >= 71 && code <= 75) return "snow";
  if (code >= 80 && code <= 82) return "rain";
  if (code >= 95) return "thunderstorm";
  return "cloudy";
}

function HomeTab() {
  const queryClient = useQueryClient();
  const { config } = usePlatformConfig();
  const personas = useOnboardingStore((s) => s.personas);
  const finishedLater = useOnboardingStore((s) => s.finishedLater);
  const setFinishedLater = useOnboardingStore((s) => s.setFinishedLater);
  const storeLiveConsent = useOnboardingStore((s) =>
    Boolean(s.consent["live_location_while_open"]),
  );
  const primary = personas[0] || "resident";

  // Profile completion modal
  const [missingFields, setMissingFields] = useState<MissingFields | null>(null);
  const [showModal, setShowModal] = useState(false);

  // On mount: check what the logged-in user is actually missing from the server
  useEffect(() => {
    let cancelled = false;
    async function checkProfile() {
      try {
        const me = await authApi.me();
        if (cancelled) return;
        const missing: MissingFields = {
          phone: !me.user.phone,
          email: !me.user.email,
        };
        // Show modal if anything is missing, OR if they tapped "Finish later" in onboarding
        if (missing.phone || missing.email || finishedLater) {
          setMissingFields(missing);
          // Only auto-open if something is actually missing
          if (missing.phone || missing.email) setShowModal(true);
        }
      } catch {
        // Non-fatal — don't block home screen
      }
    }
    void checkProfile();
    return () => { cancelled = true; };
  // Only run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const [liveStalePrompt, setLiveStalePrompt] = useState(false);
  const [here, setHere] = useState<{ ward: string; county: string } | null>(null);
  const [gpsFix, setGpsFix] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsState, setGpsState] = useState<"locating" | "live" | "denied">("locating");
  const lastPostedRef = useRef<{ at: number; lat: number; lng: number } | null>(null);
  const postingRef = useRef(false);

  const live = useQuery({
    queryKey: ["user", "live", "overview"],
    queryFn: () => liveApi.userOverview(),
    retry: 1,
    refetchInterval: isOnline ? 30_000 : false,
  });

  const placeRisks = useQuery({
    queryKey: ["user", "live", "place-risks"],
    queryFn: () => liveApi.userPlaceRisks(),
    retry: 1,
    refetchInterval: isOnline ? 30_000 : false,
  });

  const locationStatus = useQuery({
    queryKey: ["user", "location", "status"],
    queryFn: () => locationApi.getLocationStatus(),
    retry: 1,
    refetchInterval: 60_000,
  });

  const weather = useQuery({
    queryKey: ["user", "weather", "current"],
    queryFn: () => weatherApi.current(gpsFix ? { lat: gpsFix.lat, lng: gpsFix.lng } : undefined),
    retry: 1,
    refetchInterval: isOnline ? 10 * 60 * 1000 : false,
    enabled: isOnline,
  });

  const hourlyForecast = useQuery({
    queryKey: ["user", "weather", "hourly"],
    queryFn: () => weatherApi.hourly(gpsFix ? { lat: gpsFix.lat, lng: gpsFix.lng } : undefined),
    retry: 1,
    refetchInterval: isOnline ? 10 * 60 * 1000 : false,
    enabled: isOnline,
  });

  const liveConsent = Boolean(locationStatus.data?.liveConsent) || storeLiveConsent;
  const lastLiveAt = locationStatus.data?.lastLiveAt
    ? new Date(locationStatus.data.lastLiveAt).getTime()
    : null;

  useEffect(() => {
    if (!liveConsent || !lastLiveAt) {
      setLiveStalePrompt(false);
      return;
    }
    setLiveStalePrompt(Date.now() - lastLiveAt > LIVE_STALE_MS);
  }, [liveConsent, lastLiveAt, locationStatus.dataUpdatedAt]);

  const publishFix = async (latitude: number, longitude: number, accuracy?: number) => {
    const token = config.mapbox.accessToken;
    let county: string | undefined;
    let ward: string | undefined;
    if (token) {
      const rev = await reverseGeocodeMapbox(latitude, longitude, token).catch(() => null);
      if (rev) {
        county = rev.county;
        ward = rev.ward;
      }
    }
    const posted = await locationApi.postLiveLocation({
      latitude,
      longitude,
      ...(accuracy != null ? { accuracyM: accuracy } : {}),
      ...(county ? { county } : {}),
      ...(ward ? { ward } : {}),
      live_location_while_open: true,
    });
    const namedWard = posted.live?.ward || posted.reverse?.ward || ward || "";
    const namedCounty = posted.live?.county || posted.reverse?.county || county || "";
    if (namedWard || namedCounty) {
      setHere({ ward: namedWard, county: namedCounty });
      setGpsState("live");
    }
    setGpsFix({ lat: latitude, lng: longitude });
    setLiveStalePrompt(false);
    void queryClient.invalidateQueries({ queryKey: ["user", "location"] });
    void queryClient.invalidateQueries({ queryKey: ["user", "live"] });
    void queryClient.invalidateQueries({ queryKey: ["user", "alerts"] });
    void queryClient.invalidateQueries({ queryKey: ["user", "map"] });
  };

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGpsState("denied");
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const now = Date.now();
        const prev = lastPostedRef.current;
        const moved =
          !prev ||
          distanceMetres(
            { lat: prev.lat, lng: prev.lng },
            { lat: latitude, lng: longitude },
          ) >= LIVE_MOVE_METRES;
        const timedOut = !prev || now - prev.at >= LIVE_THROTTLE_MS;
        if (!moved && !timedOut) return;
        if (postingRef.current) return;
        postingRef.current = true;
        setGpsFix({ lat: latitude, lng: longitude });
        void publishFix(latitude, longitude, accuracy ?? undefined)
          .then(() => {
            lastPostedRef.current = { at: now, lat: latitude, lng: longitude };
          })
          .catch(() => {
            lastPostedRef.current = null;
          })
          .finally(() => {
            postingRef.current = false;
          });
      },
      () => {
        setGpsState("denied");
      },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [config.mapbox.accessToken, queryClient]);

  const serverLive = locationStatus.data?.live;
  const hereRisk = placeRisks.data?.places?.[0];
  const areaWard = here?.ward || serverLive?.ward || hereRisk?.ward || "";
  const areaCounty = here?.county || serverLive?.county || hereRisk?.county || "";
  const areaSeverity = hereRisk?.severity || null;

  const forecast = placeRisks.data?.forecast72 || [];
  const season = placeRisks.data?.season || null;
  const liveName = live.data?.user?.name;

  const firstName = liveName?.split(" ")[0] || "";
  const hour = new Date().getHours();
  const greeting =
    hour < 12
      ? `Good morning${firstName ? ", " + firstName : ""}`
      : hour < 17
        ? `Good afternoon${firstName ? ", " + firstName : ""}`
        : `Good evening${firstName ? ", " + firstName : ""}`;

  const personaHint =
    primary === "driver"
      ? "Check route risk before you leave when alerts are active."
      : primary === "rider"
        ? "Check advisories before riding in heavy rain."
        : "Watch alerts for where you are.";

  function handleRefresh() {
    void queryClient.invalidateQueries();
  }

  async function shareLocation() {
    if (!gpsFix) {
      toast.error("No GPS fix yet");
      return;
    }
    const text = `My location: ${gpsFix.lat.toFixed(5)}, ${gpsFix.lng.toFixed(5)}`;
    if (navigator.share) {
      await navigator.share({ title: "My location", text }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(text).catch(() => {});
      toast.success("Coordinates copied to clipboard");
    }
  }

  return (
    <div className="space-y-4">
      {/* Greeting + refresh button */}
      <div className="flex items-center justify-between">
        <p className="text-2xl font-bold text-foreground">{greeting}.</p>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          aria-label="Refresh"
        >
          <RefreshCw
            className={cn(
              "h-3 w-3",
              (weather.isFetching || placeRisks.isFetching) && "animate-spin",
            )}
          />
          Refresh
        </button>
      </div>

      {/* Offline banner */}
      {!isOnline && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm">
          <p className="font-bold">Offline — showing cached data</p>
          {weather.dataUpdatedAt ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Last updated at {new Date(weather.dataUpdatedAt).toLocaleTimeString()}
            </p>
          ) : null}
        </div>
      )}

      {/* Current weather card */}
      {weather.isLoading ? (
        <div className="h-32 w-full animate-pulse rounded-xl bg-muted" />
      ) : weather.data ? (
        <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <h2 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Current weather
          </h2>
          <div className="flex items-center justify-between">
            <WeatherIcon icon={weather.data.conditionIcon} size={56} />
            <div className="text-right">
              <p className="text-4xl font-bold">{Number(weather.data.temperature)}°C</p>
              <p className="text-sm text-muted-foreground">Feels like {Number(weather.data.feelsLike)}°C</p>
            </div>
          </div>
          <p className="mt-2 font-semibold">{String(weather.data.conditionText)}</p>
          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
            <span>💧 {Number(weather.data.humidity)}%</span>
            <span>🌬️ {Number(weather.data.windKph)} km/h</span>
            <span>🌧️ {Number(weather.data.rainChancePct)}% rain</span>
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">
            Updated {new Date(weather.data.lastUpdated).toLocaleTimeString()}
          </p>
        </section>
      ) : null}

      {/* Current risk card (merged location + risk) */}
      {placeRisks.isLoading ? (
        <div className="h-20 w-full animate-pulse rounded-xl bg-muted" />
      ) : (
        <section className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            {areaSeverity ? <SeverityPill severity={areaSeverity} /> : null}
            <div className="min-w-0">
              <p className="font-semibold">
                {[areaWard, areaCounty].filter(Boolean).join(", ") || "Your area"}
              </p>
              {(() => {
                const place = hereRisk;
                if (placeRisks.data?.locationRequired || gpsState === "denied") {
                  return (
                    <p className="text-sm text-muted-foreground">
                      Turn on location. Alerts follow where you are, not a saved place.
                    </p>
                  );
                }
                if (!place) {
                  return (
                    <p className="text-sm text-muted-foreground">
                      {gpsState === "locating" ? "Finding where you are…" : ""}
                    </p>
                  );
                }
                const rain = place.rain24hMm;
                const ratio = Number(place.riverDischargeRatio);
                return (
                  <p className="text-sm text-muted-foreground">
                    {Number.isFinite(ratio) && ratio >= 1.1
                      ? `River is ${ratio.toFixed(1)}× its recent level`
                      : rain != null && Number(rain) > 0
                        ? `24h rainfall: ${Number(rain).toFixed(1)}mm`
                        : "No flood advisory at your location"}
                  </p>
                );
              })()}
            </div>
          </div>
        </section>
      )}

      {/* Live stale prompt */}
      {liveStalePrompt ? (
        <div className="rounded-lg border border-watch/30 bg-watch-soft p-4">
          <p className="text-sm font-bold">Live location looks stale</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Last update was over 2 hours ago. Keep this page open to refresh, or tap Update.
          </p>
          <Button
            size="sm"
            className="mt-3"
            variant="secondary"
            onClick={() => {
              if (!navigator.geolocation) {
                toast.error("GPS not available");
                return;
              }
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  void publishFix(
                    pos.coords.latitude,
                    pos.coords.longitude,
                    pos.coords.accuracy ?? undefined,
                  )
                    .then(() => toast.success("Live location updated"))
                    .catch(() => toast.error("Could not update live location"));
                },
                () => toast.error("Could not read GPS"),
              );
            }}
          >
            Update now
          </Button>
        </div>
      ) : null}

      {/* Profile completion — shown when phone or email is missing */}
      {showModal && missingFields && (
        <ProfileCompletionModal
          missing={missingFields}
          onComplete={() => {
            setShowModal(false);
            setFinishedLater(false);
            // Refresh profile data
            void queryClient.invalidateQueries({ queryKey: ["settings", "profile"] });
          }}
          onDismiss={() => setShowModal(false)}
        />
      )}

      {/* Soft banner when profile is incomplete but modal was dismissed */}
      {!showModal && missingFields && (missingFields.phone || missingFields.email) && (
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="w-full rounded-lg border border-warning/30 bg-warning-soft p-4 text-left"
        >
          <p className="text-sm font-bold">Complete your profile</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {missingFields.phone && missingFields.email
              ? "Add your phone and email to receive alerts on all channels."
              : missingFields.phone
                ? "Add a phone number to receive SMS and WhatsApp alerts."
                : "Add an email address to receive email alerts."}
          </p>
          <p className="mt-2 text-xs font-semibold text-primary">Tap to complete →</p>
        </button>
      )}

      {/* Hourly rain strip — next 5 hours */}
      {hourlyForecast.data?.hours?.length ? (
        <section>
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Next 5 hours
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {hourlyForecast.data.hours.slice(0, 5).map((h) => (
              <div key={h.hour} className="flex min-w-[52px] flex-col items-center gap-1">
                <span className="text-[10px] text-muted-foreground">{h.hour}</span>
                <WeatherIcon icon={wmoToIcon(h.conditionCode)} size={22} />
                <span className="text-[10px] font-semibold">{Number(h.rainChancePct)}%</span>
                <div className="h-1 w-full overflow-hidden rounded bg-muted">
                  <div
                    className="h-full rounded bg-primary"
                    style={{ width: `${Number(h.rainChancePct)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* 3-day forecast — always open */}
      {placeRisks.data?.forecast72?.length ? (
        <section className="rounded-xl border border-border bg-card">
          <h2 className="px-4 pb-2 pt-3 font-display text-base font-bold">3-day outlook</h2>
          <div className="grid grid-cols-1 gap-3 px-4 pb-4 sm:grid-cols-3">
            {placeRisks.data.forecast72.map((f, i) => {
              const risk = String(f.risk || "LOW").toUpperCase();
              const dayLabel = i === 0 ? "Today" : i === 1 ? "Tomorrow" : "Day after";
              return (
                <div key={f.hour} className="rounded-md border border-border bg-muted/40 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {dayLabel}
                  </p>
                  <p className="mt-2 font-display text-2xl font-bold tabular-nums">
                    {Number(f.rainMm ?? 0).toFixed(1)}
                    <span className="ml-1 text-sm font-semibold text-muted-foreground">
                      mm rain
                    </span>
                  </p>
                  <p className="mt-2 text-sm font-semibold">{String(f.level || "")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{String(f.hint || "")}</p>
                  <span
                    className={cn(
                      "mt-3 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase",
                      severityStyle[risk] || severityStyle["WATCH"],
                    )}
                  >
                    Flood risk:{" "}
                    {risk === "LOW" ? "Low" : risk.charAt(0) + risk.slice(1).toLowerCase()}
                  </span>
                  {risk !== "LOW" && (
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {f.reason === "river"
                        ? "Flood risk is from the river, not rainfall alone"
                        : risk === "SEVERE"
                          ? "Severe flood risk where you are"
                          : risk === "WARNING"
                            ? "Warning where you are"
                            : "Watch where you are"}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Season / advisory banner */}
      {season ? (
        <div className="rounded-lg border border-primary/20 bg-primary-soft px-4 py-3 text-sm">
          <p className="font-bold">{season.label}</p>
          <p className="mt-1 text-xs text-muted-foreground">{season.banner}</p>
        </div>
      ) : null}

      {/* Persona hint */}
      <PersonaHint persona={primary} hint={personaHint} />

      {/* Active alerts count */}
      {(() => {
        const alertCount = live.data?.activeAlerts?.length || 0;
        return alertCount > 0 ? (
          <Link
            to="/app/alerts"
            className="flex items-center gap-2 rounded-xl border border-warning/40 bg-warning-soft px-4 py-3 text-sm font-semibold text-warning"
          >
            ⚠️ {alertCount} active alert{alertCount > 1 ? "s" : ""} — tap to view
          </Link>
        ) : null;
      })()}

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-3">
        <Link
          to="/app/report"
          className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card p-3 text-center text-xs font-semibold hover:bg-muted"
        >
          <span className="text-lg">📸</span>
          Report hazard
        </Link>
        <Link
          to="/app/search"
          className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card p-3 text-center text-xs font-semibold hover:bg-muted"
        >
          <span className="text-lg">🗺️</span>
          Plan route
        </Link>
        <button
          onClick={shareLocation}
          className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card p-3 text-center text-xs font-semibold hover:bg-muted"
        >
          <span className="text-lg">📍</span>
          Share location
        </button>
      </div>

      {/* Emergency numbers */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="mb-3 font-display text-base font-bold">Emergency numbers</h2>
        <div className="space-y-2">
          {EMERGENCY_NUMBERS.map((n) => (
            <a
              key={n.number}
              href={`tel:${n.number}`}
              className="flex items-center gap-3 text-sm font-semibold"
            >
              <Phone className="h-4 w-4 text-primary" />
              {n.label}: {n.number}
            </a>
          ))}
          <a href="tel:0800720999" className="flex items-center gap-3 text-sm font-semibold">
            <Phone className="h-4 w-4 text-primary" />
            {areaCounty ? `${areaCounty} County Emergency` : "County Emergency"}: 0800 720 999
          </a>
        </div>
      </section>

      {/* Last updated */}
      {weather.dataUpdatedAt ? (
        <p className="text-center text-[10px] text-muted-foreground">
          Data last refreshed: {new Date(weather.dataUpdatedAt).toLocaleTimeString()}
        </p>
      ) : null}
    </div>
  );
}

function PersonaHint({ persona, hint }: { persona: string; hint?: string }) {
  const Icon = persona === "driver" ? Car : persona === "rider" ? Bike : Home;
  const title =
    persona === "driver" ? "Drive safely" : persona === "rider" ? "Ride safely" : "Home risk";
  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="font-display text-base font-bold">{title}</h2>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{hint}</p>
    </section>
  );
}
